import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { UserWithProfile } from "@/pages/Users";
import type { UserFormData } from "./types";

export function useUserUpdate(user: UserWithProfile, onUserUpdated: () => Promise<void>) {
  const [isLoading, setIsLoading] = useState(false);

  const updateUser = async (formData: UserFormData) => {
    try {
      console.log("Starting user update process...");
      console.log("Current user data:", {
        id: user.id,
        ...formData
      });
      
      setIsLoading(true);

      // Call the Edge Function to update user profile and auth
      console.log("Calling update-user Edge Function...");
      const { data: updateData, error: updateError } = await supabase.functions.invoke('update-user', {
        body: {
          userId: user.id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          currentEmail: user.profiles?.email
        }
      });

      if (updateError) {
        console.error("Error from Edge Function:", updateError);
        throw updateError;
      }

      console.log("Edge Function response:", updateData);

      const currentRole = user.user_roles[0]?.role ?? "user";
      
      // Update role if changed
      if (formData.role !== currentRole) {
        console.log("Role change detected, updating from", currentRole, "to", formData.role);
        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .update({ role: formData.role })
          .eq("user_id", user.id)
          .select()
          .maybeSingle();

        console.log("Role update response:", { roleData, roleError });

        if (roleError) {
          console.error("Error updating role:", roleError);
          throw roleError;
        }

        if (!roleData) {
          console.error("No role was updated");
          throw new Error("Aucun rôle n'a été mis à jour");
        }
      }

      console.log("All updates completed successfully, calling onUserUpdated...");
      await onUserUpdated();
      console.log("Data refresh completed");
      
      // Show different toast messages based on email change
      if (formData.email !== user.profiles?.email) {
        toast.success("Utilisateur mis à jour. Un email de confirmation a été envoyé à la nouvelle adresse.");
      } else {
        toast.success("Utilisateur mis à jour avec succès");
      }
      
      return true;
    } catch (error) {
      console.error("Error in updateUser:", error);
      toast.error("Erreur lors de la mise à jour de l'utilisateur");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    updateUser
  };
}